import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { MagnifyingGlass, UserPlus, X, User } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function CustomerCombobox({ value, onChange, onCreate }) {
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newCust, setNewCust] = useState({ name: "", mobile: "", gst_number: "", email: "" });
  const wrapRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/customers");
    setCustomers(data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const off = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", off);
    return () => document.removeEventListener("mousedown", off);
  }, []);

  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    if (!s) return customers.slice(0, 8);
    return customers
      .filter((c) => c.name?.toLowerCase().includes(s) || c.mobile?.toLowerCase().includes(s))
      .slice(0, 12);
  }, [customers, q]);

  const select = (c) => {
    onChange?.(c);
    setQ("");
    setOpen(false);
  };

  const saveNew = async () => {
    if (!newCust.name.trim() || !newCust.mobile.trim()) { toast.error("Name and mobile required"); return; }
    try {
      const payload = { ...newCust };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      payload.name = newCust.name; payload.mobile = newCust.mobile;
      const { data } = await api.post("/customers", payload);
      toast.success("Customer added");
      setCustomers((arr) => [data, ...arr]);
      onChange?.(data);
      onCreate?.(data);
      setAddOpen(false);
      setNewCust({ name: "", mobile: "", gst_number: "", email: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="relative" ref={wrapRef}>
      {value ? (
        <div data-testid="pos-customer-pill" className="flex items-center justify-between rounded-md border border-border bg-background px-3 h-10">
          <div className="flex items-center gap-2 min-w-0">
            <User size={14} className="text-gold shrink-0" weight="duotone" />
            <div className="truncate text-sm">
              <span className="font-medium">{value.name}</span>
              <span className="text-muted-foreground"> · {value.mobile}</span>
              {value.gst_number ? <span className="text-muted-foreground"> · GST {value.gst_number}</span> : null}
            </div>
          </div>
          <button data-testid="pos-customer-clear" onClick={() => onChange?.(null)} className="text-muted-foreground hover:text-foreground p-1"><X size={14} /></button>
        </div>
      ) : (
        <>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="pos-customer-input"
              value={q}
              onChange={(e) => { setQ(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              placeholder="Search customer by name or mobile..."
              className="pl-9 pr-24 bg-background border-border h-10"
            />
            <button
              data-testid="pos-customer-add"
              onClick={() => { setNewCust({ ...newCust, name: q.match(/[a-zA-Z]/) ? q : "", mobile: q.match(/^\d+$/) ? q : "" }); setAddOpen(true); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-xs px-2 py-1 rounded-md bg-primary/15 border border-primary/30 text-gold hover:bg-primary/25"
            >
              <UserPlus size={12} className="inline mr-1" /> New
            </button>
          </div>
          {open && (filtered.length > 0 || q.length > 0) && (
            <div data-testid="pos-customer-dropdown" className="absolute z-30 mt-1 w-full max-h-64 overflow-y-auto rounded-md border border-border bg-card shadow-lg">
              {filtered.length === 0 ? (
                <div className="p-3 text-sm text-muted-foreground">
                  No match. <button onClick={() => { setNewCust({ ...newCust, mobile: q.match(/^\d+$/) ? q : "", name: q.match(/[a-zA-Z]/) ? q : "" }); setAddOpen(true); }} className="text-gold hover:underline">+ Add "{q}"</button>
                </div>
              ) : (
                filtered.map((c) => (
                  <button
                    key={c.id}
                    data-testid={`pos-customer-option-${c.id}`}
                    onClick={() => select(c)}
                    className="w-full text-left px-3 py-2 hover:bg-accent flex items-center gap-3 border-b border-border last:border-0"
                  >
                    <div className="h-8 w-8 rounded-full bg-primary/10 border border-primary/30 grid place-items-center text-gold shrink-0">
                      <User size={12} weight="duotone" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{c.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{c.mobile}{c.gst_number ? ` · GST ${c.gst_number}` : ""}</div>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader><DialogTitle className="font-display text-xl">Add Customer</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">Name *</Label>
              <Input data-testid="quick-cust-name" className="mt-1.5 bg-background border-border" value={newCust.name} onChange={(e) => setNewCust({ ...newCust, name: e.target.value })} />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">Mobile *</Label>
              <Input data-testid="quick-cust-mobile" className="mt-1.5 bg-background border-border" value={newCust.mobile} onChange={(e) => setNewCust({ ...newCust, mobile: e.target.value })} />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">GST Number (optional)</Label>
              <Input data-testid="quick-cust-gst" className="mt-1.5 bg-background border-border" value={newCust.gst_number} onChange={(e) => setNewCust({ ...newCust, gst_number: e.target.value })} />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">Email (optional)</Label>
              <Input className="mt-1.5 bg-background border-border" value={newCust.email} onChange={(e) => setNewCust({ ...newCust, email: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button data-testid="quick-cust-save" onClick={saveNew} className="bg-primary text-primary-foreground hover:bg-primary/90">Add & select</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
